// Self-moving auras. Wildfire pattern: at the end of each turn, the
// aura's controller moves it to an adjacent site cell it hasn't visited
// before, or it dispels if no fresh ground remains. Visited cells live
// on `card._visitedCells`. The destination is a player choice (cell
// pick), so the silent op just hands off to the host.
//
// Pure cell picker + host-dispatched move. Host adapter contract:
//   meshesHas(id): boolean
//   routeWildfireMove(card, controller): void  // opens the controller's pick

import { OP } from '../schema';
import { AURA_SHAPE, anchorCoveredCells } from '../../core/auraPlacement';
void OP;

const CARDINAL = [[0, -1], [0, 1], [-1, 0], [1, 0]];

/**
 * Index every Site-bearing cell by `'col,row'` for quick lookup of
 * "is there a site here?" + the site's world position.
 */
function sitesByCellKey(meshes, cellAt) {
  const out = new Map();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    out.set(`${cell.col},${cell.row}`, { card, position: mesh.position });
  }
  return out;
}

/**
 * Every cardinal-adjacent site cell the source hasn't visited yet, in
 * deterministic order (N, S, W, E) so replay parity holds. The player
 * picks one of these as Wildfire's destination.
 */
export function unvisitedAdjacentSiteCells(sourceCard, meshes, cellAt) {
  if (!sourceCard?._auraAnchor) return [];
  const { col, row } = sourceCard._auraAnchor;
  const visited = new Set(sourceCard._visitedCells || []);
  // Lazy-add the source's own cell so it's never offered as a target.
  visited.add(`${col},${row}`);
  const sites = sitesByCellKey(meshes, cellAt);
  const out = [];
  for (const [dc, dr] of CARDINAL) {
    const nc = col + dc;
    const nr = row + dr;
    const key = `${nc},${nr}`;
    if (visited.has(key)) continue;
    const site = sites.get(key);
    if (!site) continue;
    out.push({ col: nc, row: nr, position: site.position });
  }
  return out;
}

/**
 * First unvisited adjacent site cell, or `null`. Retained for any
 * caller that wants the deterministic auto-pick rather than a choice.
 */
export function findUnvisitedAdjacentSiteCell(sourceCard, meshes, cellAt) {
  return unvisitedAdjacentSiteCells(sourceCard, meshes, cellAt)[0] || null;
}

/**
 * Silent-op entry for `MOVE_AURA_TO_UNVISITED_ADJACENT`. The move is a
 * player choice, so this delegates to the host, which opens a cell pick
 * for the aura's controller (or relays the request cross-client when the
 * controller is the remote player). Host contract: `routeWildfireMove`.
 */
export function applyMoveAuraToUnvisitedAdjacent(sourceCard, host, controller) {
  if (!host.meshesHas(sourceCard.id)) return;
  host.routeWildfireMove(sourceCard, controller);
}

// ── MOVE_AURA_ONE_STEP (Thunderstorm) ───────────────────────────────
//
// A "step" shifts an INTERSECTION_4 anchor one intersection in a
// cardinal direction. The click targets are the cells the aura would
// NEWLY cover in each direction (a 2-cell strip beyond the current
// 2×2 area). The four strips never overlap, so a picked cell resolves
// to exactly one destination anchor. And the pick reads naturally as
// "click where the storm should spread."

const STEP_DIRS = [
  { dc: 0,  dr: -1 },
  { dc: 0,  dr: 1 },
  { dc: -1, dr: 0 },
  { dc: 1,  dr: 0 },
];

function isLegalAnchor(col, row, totalCols, totalRows) {
  return col >= 1 && col <= totalCols - 1 && row >= 1 && row <= totalRows - 1;
}

/**
 * Every legal one-step destination for an INTERSECTION_4 anchor.
 * Returns `{ anchor: { col, row }, cells: ['c,r', ...] }[]` where
 * `cells` is the newly covered strip used as the cell-pick targets.
 */
export function auraStepCandidates(anchor, totalCols, totalRows) {
  if (!anchor) return [];
  const covered = new Set(anchorCoveredCells(anchor, AURA_SHAPE.INTERSECTION_4));
  const out = [];
  for (const { dc, dr } of STEP_DIRS) {
    const col = anchor.col + dc;
    const row = anchor.row + dr;
    if (!isLegalAnchor(col, row, totalCols, totalRows)) continue;
    const next = { col, row };
    const cells = anchorCoveredCells(next, AURA_SHAPE.INTERSECTION_4)
      .filter((key) => !covered.has(key));
    out.push({ anchor: next, cells });
  }
  return out;
}

/**
 * Map a picked cell back to the destination anchor whose newly covered
 * strip contains it, or `null` when the cell isn't in any strip.
 */
export function auraStepAnchorForCell(anchor, cell, totalCols, totalRows) {
  if (!cell) return null;
  const key = `${cell.col},${cell.row}`;
  for (const cand of auraStepCandidates(anchor, totalCols, totalRows)) {
    if (cand.cells.includes(key)) return cand.anchor;
  }
  return null;
}
